import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';

const PrivacyPolicy = () => {
  const lastUpdated = "January 13, 2026";

  return (
    <div className="min-h-screen bg-white">
      <Header isAuthenticated={false} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-8">
          <div className="bg-teal-50 border-l-4 border-teal-500 p-6 mb-8 rounded-r-lg">
            <p className="text-gray-800 font-medium mb-2">
              Your privacy and safety are our top priorities.
            </p>
            <p className="text-gray-700 text-sm">
              At WanderVibe, we are committed to protecting your personal information and being transparent about the data we collect. This Privacy Policy explains how we collect, use, share, and protect your information when you use our women's travel safety platform.
            </p>
          </div>

          {/* Quick Navigation */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <a href="#information-we-collect" className="text-teal-600 hover:text-teal-700 hover:underline">1. Information We Collect</a>
              <a href="#how-we-use" className="text-teal-600 hover:text-teal-700 hover:underline">2. How We Use Your Information</a>
              <a href="#information-sharing" className="text-teal-600 hover:text-teal-700 hover:underline">3. Information Sharing</a>
              <a href="#data-security" className="text-teal-600 hover:text-teal-700 hover:underline">4. Data Security</a>
              <a href="#your-rights" className="text-teal-600 hover:text-teal-700 hover:underline">5. Your Rights</a>
              <a href="#international" className="text-teal-600 hover:text-teal-700 hover:underline">6. International Transfers</a>
              <a href="#cookies" className="text-teal-600 hover:text-teal-700 hover:underline">7. Cookies & Tracking</a>
              <a href="#children" className="text-teal-600 hover:text-teal-700 hover:underline">8. Children's Privacy</a>
              <a href="#changes" className="text-teal-600 hover:text-teal-700 hover:underline">9. Changes to Policy</a>
              <a href="#contact" className="text-teal-600 hover:text-teal-700 hover:underline">10. Contact Us</a>
            </div>
          </div>

          {/* 1. Information We Collect */}
          <section id="information-we-collect" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              1. Information We Collect
            </h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">1.1 Information You Provide</h3>
            <div className="space-y-4 ml-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Account Information:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Email address</li>
                  <li>Username and password</li>
                  <li>Profile information (optional: name, bio, profile photo)</li>
                  <li>Age verification (confirmation that you're 18+)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Safety Features:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Emergency contact information (names, phone numbers)</li>
                  <li>Location data when you use safety features</li>
                  <li>Trip plans and saved locations</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">User-Generated Content:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Reviews and ratings of locations</li>
                  <li>Comments and feedback</li>
                  <li>Safety reports and alerts</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Payment Information:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>We use Stripe for payment processing</li>
                  <li>We do NOT store your credit card information</li>
                  <li>Stripe retains payment information per their privacy policy</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">1.2 Information We Collect Automatically</h3>
            <div className="space-y-4 ml-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Usage Information:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Pages you visit and features you use</li>
                  <li>Search queries and filters applied</li>
                  <li>Time spent on different sections</li>
                  <li>Device information (browser, OS, device type)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Location Information:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Approximate location from IP address</li>
                  <li>Precise location (only when you grant permission)</li>
                  <li>Location history (only for safety features you enable)</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2 italic">
                  Note: You can disable location tracking at any time in your device settings.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Technical Information:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Referring website</li>
                  <li>Error logs and performance data</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 2. How We Use Your Information */}
          <section id="how-we-use" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              2. How We Use Your Information
            </h2>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">🛡️ Safety & Security:</h3>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Provide real-time safety alerts for your current location</li>
                  <li>Enable emergency SOS features and contact emergency contacts</li>
                  <li>Show safety ratings and reviews for destinations</li>
                  <li>Detect and prevent fraud, spam, and abuse</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">✨ Service Delivery:</h3>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Create and manage your account</li>
                  <li>Provide personalized recommendations</li>
                  <li>Enable trip planning and location saving</li>
                  <li>Process subscription payments</li>
                  <li>Send important account notifications</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">📊 Improvement & Analytics:</h3>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Analyze how users interact with our platform</li>
                  <li>Improve our safety algorithms and recommendations</li>
                  <li>Fix bugs and optimize performance</li>
                  <li>Understand which features are most valuable</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2 italic">
                  Note: We use aggregated, anonymized data for analytics. We never sell individual user data.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">📧 Communication:</h3>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Send safety alerts relevant to your location</li>
                  <li>Notify you of account activity</li>
                  <li>Respond to your support requests</li>
                  <li>Send marketing emails (only if you opt in)</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2 italic">
                  You can unsubscribe from marketing emails at any time.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2">⚖️ Legal Compliance:</h3>
                <ul className="list-disc ml-6 text-gray-700 space-y-1">
                  <li>Comply with legal obligations</li>
                  <li>Enforce our Terms of Service</li>
                  <li>Respond to legal requests</li>
                  <li>Protect our rights and the rights of users</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 3. Information Sharing */}
          <section id="information-sharing" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              3. Information Sharing & Disclosure
            </h2>

            <div className="bg-pink-50 border-l-4 border-pink-500 p-4 mb-4 rounded-r-lg">
              <p className="font-semibold text-gray-900 mb-1">We do NOT sell your personal information</p>
              <p className="text-sm text-gray-700">
                Your data is yours. We will never sell it to third parties for marketing purposes.
              </p>
            </div>

            <div className="space-y-4 ml-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">We Share Information With:</h3>

                <div className="mt-3">
                  <h4 className="font-semibold text-gray-700 mb-1">Service Providers:</h4>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li><strong>Stripe</strong> (payment processing)</li>
                    <li><strong>MongoDB Atlas</strong> (database hosting)</li>
                    <li><strong>Heroku</strong> (application hosting)</li>
                    <li><strong>Sentry</strong> (error monitoring - anonymized data)</li>
                    <li><strong>Email service providers</strong> (for transactional emails)</li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-2 italic">
                    All service providers are bound by confidentiality agreements and may only use your data to provide services to us.
                  </p>
                </div>

                <div className="mt-3">
                  <h4 className="font-semibold text-gray-700 mb-1">Other Users (Only Public Information):</h4>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Your username and profile photo (if provided)</li>
                    <li>Reviews and ratings you post publicly</li>
                    <li>Comments on public posts</li>
                  </ul>
                  <p className="text-xs text-gray-600 mt-2 italic">
                    Note: Your email, location data, and emergency contacts are NEVER shared publicly.
                  </p>
                </div>

                <div className="mt-3">
                  <h4 className="font-semibold text-gray-700 mb-1">Emergency Situations:</h4>
                  <p className="text-sm text-gray-700 mb-1">
                    If you use our Emergency SOS feature, we may share your location and emergency contact information with:
                  </p>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Your designated emergency contacts</li>
                    <li>Local emergency services (if requested)</li>
                    <li>Nearby WanderVibe users (if you enable community alerts)</li>
                  </ul>
                </div>

                <div className="mt-3">
                  <h4 className="font-semibold text-gray-700 mb-1">Legal Requirements:</h4>
                  <p className="text-sm text-gray-700 mb-1">
                    We may disclose your information if required by law or in response to:
                  </p>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                    <li>Valid legal requests (subpoenas, court orders)</li>
                    <li>Protection of our legal rights</li>
                    <li>Investigation of fraud or security issues</li>
                    <li>Compliance with legal obligations</li>
                  </ul>
                </div>

                <div className="mt-3">
                  <h4 className="font-semibold text-gray-700 mb-1">Business Transfers:</h4>
                  <p className="text-sm text-gray-700">
                    If WanderVibe is involved in a merger, acquisition, or sale of assets, your information may be transferred. We will notify you before your information is transferred and becomes subject to a different privacy policy.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 4. Data Security */}
          <section id="data-security" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              4. Data Security
            </h2>

            <p className="text-gray-700 mb-4">
              We take security seriously and implement industry-standard measures to protect your information:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">🔒 Encryption:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>HTTPS/SSL for all data transmission</li>
                  <li>Encrypted database storage</li>
                  <li>Hashed passwords (bcrypt)</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">🛡️ Access Controls:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>Limited employee access</li>
                  <li>Two-factor authentication for admin accounts</li>
                  <li>Regular security audits</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">🔍 Monitoring:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>24/7 system monitoring</li>
                  <li>Automated threat detection</li>
                  <li>Regular vulnerability scanning</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">📱 Secure Infrastructure:</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>Secure cloud hosting (Heroku)</li>
                  <li>Regular backups</li>
                  <li>DDoS protection</li>
                </ul>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <p className="text-sm text-gray-800">
                <strong>Important:</strong> No method of transmission over the Internet is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security. Please use a strong, unique password and enable two-factor authentication when available.
              </p>
            </div>
          </section>

          {/* 5. Your Rights */}
          <section id="your-rights" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              5. Your Privacy Rights
            </h2>

            <p className="text-gray-700 mb-4">
              You have the following rights regarding your personal information:
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-teal-300 pl-4">
                <h4 className="font-semibold text-gray-800 mb-1">✅ Access Your Data</h4>
                <p className="text-sm text-gray-700">
                  You can request a copy of all data we have about you. Go to Settings → Privacy → Download My Data.
                </p>
              </div>

              <div className="border-l-4 border-teal-300 pl-4">
                <h4 className="font-semibold text-gray-800 mb-1">✏️ Correct Your Data</h4>
                <p className="text-sm text-gray-700">
                  Update your account information anytime in your Profile Settings.
                </p>
              </div>

              <div className="border-l-4 border-teal-300 pl-4">
                <h4 className="font-semibold text-gray-800 mb-1">🗑️ Delete Your Data</h4>
                <p className="text-sm text-gray-700">
                  Request account deletion at Settings → Privacy → Delete Account. This will permanently delete your account and associated data within 30 days.
                </p>
              </div>

              <div className="border-l-4 border-teal-300 pl-4">
                <h4 className="font-semibold text-gray-800 mb-1">📍 Control Location Tracking</h4>
                <p className="text-sm text-gray-700">
                  Disable location services anytime in your device settings or app settings.
                </p>
              </div>

              <div className="border-l-4 border-teal-300 pl-4">
                <h4 className="font-semibold text-gray-800 mb-1">📧 Opt Out of Marketing</h4>
                <p className="text-sm text-gray-700">
                  Unsubscribe from marketing emails using the link at the bottom of any marketing email.
                </p>
              </div>

              <div className="border-l-4 border-teal-300 pl-4">
                <h4 className="font-semibold text-gray-800 mb-1">🍪 Manage Cookies</h4>
                <p className="text-sm text-gray-700">
                  Adjust cookie preferences in your browser settings or using our cookie consent banner.
                </p>
              </div>
            </div>

            <div className="mt-6 bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">For EU/UK Users (GDPR Rights):</h4>
              <p className="text-sm text-gray-700 mb-2">
                If you're located in the European Union or United Kingdom, you have additional rights under GDPR:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>Right to data portability (export data in machine-readable format)</li>
                <li>Right to restriction of processing</li>
                <li>Right to object to processing</li>
                <li>Right to lodge a complaint with your local data protection authority</li>
              </ul>
            </div>

            <div className="mt-4 bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">For California Users (CCPA Rights):</h4>
              <p className="text-sm text-gray-700 mb-2">
                California residents have additional rights under CCPA:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>Right to know what personal information is collected</li>
                <li>Right to know if personal information is sold or disclosed</li>
                <li>Right to opt-out of sale of personal information (we don't sell your data)</li>
                <li>Right to non-discrimination for exercising CCPA rights</li>
              </ul>
            </div>
          </section>

          {/* 6. International Transfers */}
          <section id="international" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              6. International Data Transfers
            </h2>

            <p className="text-gray-700 mb-4">
              WanderVibe is based in the United States. If you access our service from outside the US, your information may be transferred to, stored, and processed in the United States and other countries.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 mb-2">
                We ensure appropriate safeguards are in place for international transfers:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>Standard contractual clauses approved by the EU Commission</li>
                <li>Privacy Shield Framework compliance (where applicable)</li>
                <li>Adequate data protection measures as required by GDPR</li>
              </ul>
            </div>
          </section>

          {/* 7. Cookies */}
          <section id="cookies" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              7. Cookies & Tracking Technologies
            </h2>

            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Essential Cookies (Required):</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>Authentication (keep you logged in)</li>
                  <li>Security (prevent fraud)</li>
                  <li>Session management</li>
                </ul>
                <p className="text-xs text-gray-600 mt-1 italic">These cookies are necessary for the service to function.</p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Functional Cookies (Optional):</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>Remember your preferences</li>
                  <li>Save your location settings</li>
                  <li>Personalize your experience</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Analytics Cookies (Optional):</h4>
                <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                  <li>Understand how you use our service</li>
                  <li>Measure feature effectiveness</li>
                  <li>Improve our platform</li>
                </ul>
                <p className="text-xs text-gray-600 mt-1 italic">We use anonymized data for analytics.</p>
              </div>
            </div>

            <p className="text-sm text-gray-700 mt-4">
              You can control cookies through your browser settings or our cookie consent banner. Disabling essential cookies may impact site functionality.
            </p>
          </section>

          {/* 8. Children's Privacy */}
          <section id="children" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              8. Children's Privacy
            </h2>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
              <p className="font-semibold text-gray-900 mb-2">Age Requirement: 18+</p>
              <p className="text-sm text-gray-700">
                WanderVibe is intended for users aged 18 and older. We do not knowingly collect personal information from anyone under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately at <a href="mailto:privacy@wandervibe.com" className="text-teal-600 hover:underline">privacy@wandervibe.com</a>, and we will delete their information within 30 days.
              </p>
            </div>
          </section>

          {/* 9. Changes to This Policy */}
          <section id="changes" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              9. Changes to This Privacy Policy
            </h2>

            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">When we make changes:</h4>
              <ul className="list-disc ml-6 text-gray-700 space-y-1 text-sm">
                <li>We'll update the "Last Updated" date at the top of this page</li>
                <li>For significant changes, we'll notify you via email</li>
                <li>We'll post a notice on our website</li>
                <li>You'll be asked to review and accept the updated policy</li>
              </ul>
              <p className="text-sm text-gray-700 mt-3">
                Your continued use of WanderVibe after changes are posted constitutes acceptance of the updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* 10. Contact Us */}
          <section id="contact" className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-teal-500 pb-2">
              10. Contact Us
            </h2>

            <p className="text-gray-700 mb-4">
              If you have questions, concerns, or requests regarding this Privacy Policy or your personal information:
            </p>

            <div className="bg-teal-50 p-6 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3">Privacy Team Contact:</h4>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-gray-800">Email:</span>{' '}
                  <a href="mailto:privacy@wandervibe.com" className="text-teal-600 hover:underline">
                    privacy@wandervibe.com
                  </a>
                </div>

                <div>
                  <span className="font-semibold text-gray-800">Support:</span>{' '}
                  <a href="mailto:support@wandervibe.com" className="text-teal-600 hover:underline">
                    support@wandervibe.com
                  </a>
                </div>

                <div>
                  <span className="font-semibold text-gray-800">Mailing Address:</span>
                  <div className="ml-4 mt-1 text-gray-700">
                    WanderVibe, Inc.<br />
                    Privacy Team<br />
                    [Address Line 1]<br />
                    [City, State ZIP]<br />
                    United States
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-600 mt-4 italic">
                We aim to respond to all privacy inquiries within 30 days.
              </p>
            </div>
          </section>

          {/* Back to Top */}
          <div className="text-center mt-8 pt-6 border-t border-gray-200">
            <a
              href="#top"
              className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              Back to Top
            </a>
          </div>

          {/* Legal Links */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600 mb-3">Related Legal Documents:</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/terms" className="text-teal-600 hover:underline">Terms of Service</Link>
              <span className="text-gray-400">|</span>
              <Link to="/cookies" className="text-teal-600 hover:underline">Cookie Policy</Link>
              <span className="text-gray-400">|</span>
              <Link to="/" className="text-teal-600 hover:underline">Home</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
